import React, { useState, useEffect } from "react";
import {
  Box,
  Input,
  Text,
  Button,
  Flex,
  useToast,
  Container,
  VStack,
  FormLabel,
  FormControl,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Heading,
  Divider,
} from "@chakra-ui/react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import * as mod from "../../url";
import { formats, modules } from "../../utils/quillModules";
import Navbar from "../Navbar/Navbar";
import Sidebar from "../Sidebar";

const storedData = JSON.parse(sessionStorage.getItem("superAdminLawvs"));
const token = storedData?.data?.token;
const config = { headers: { Authorization: `${token}` } };

const SIDEBAR_WIDTH = "250px";

const UpdateVideo = () => {
  const { _id } = useParams(); // video id from route
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // form states
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [url, setUrl] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // fetch video by id
  useEffect(() => {
    const fetchVideoById = async () => {
      try {
        const res = await axios.get(
          `${mod.api_url}/api/v1/video/get-single-video/${_id}`,
          config
        );
        const data = res?.data?.data;

        setTitle(data.title);
        setSubtitle(data.subtitle);
        setUrl(data.videoUrl);
      } catch (err) {
        console.error("Error fetching video:", err);
        toast({
          title: "Error",
          description: "Failed to load video details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchVideoById();
  }, [_id]);

  // handle update
  const handleUpdate = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(
        `${mod.api_url}/api/v1/video/update-video/${_id}`,
        {
          title,
          subtitle,
          videoUrl: url,
        },
        config
      );

      toast({
        title: "Video updated.",
        description: "Your video was successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/videos"); // redirect to videos list page
    } catch (error) {
      console.error(
        "Error updating video:",
        error.response?.data || error.message
      );
      toast({
        title: "Update failed.",
        description: "Something went wrong while updating the video.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" h="100vh" bg="gray.50">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
      </Flex>
    );
  }

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        mt="70px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
        p={6}
        bg="gray.50"
        minH="100vh"
      >
        <Container maxW="3xl">
          <Card borderRadius="2xl" boxShadow="xl" bg="white">
            <CardHeader textAlign="center" pb={0}>
              <Heading size="lg" color="blue.600">
                ✏️ Update Video
              </Heading>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Modify the details below and save your changes
              </Text>
            </CardHeader>
            <Divider mt={4} />

            <CardBody>
              <VStack spacing={6} align="stretch">
                <FormControl>
                  <FormLabel fontWeight="bold">Video Title</FormLabel>
                  <Input
                    placeholder="Enter Video Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Video URL</FormLabel>
                  <Input
                    placeholder="Paste YouTube video URL"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    focusBorderColor="blue.400"
                  />
                </FormControl>
                <Box>
                  <FormLabel fontWeight="bold">Video Subtitle</FormLabel>
                  <ReactQuill
                    value={subtitle}
                    onChange={setSubtitle}
                    modules={modules}
                    formats={formats}
                    theme="snow"
                    placeholder="Write subtitle..."
                    style={{
                      height: "200px",
                      marginBottom: "40px",
                      background: "white",
                      borderRadius: "8px",
                    }}
                  />
                </Box>

                {/* ✅ Live Video Preview */}
                {url && (
                  <Box
                    mt={4}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden"
                    bg="gray.50"
                    p={3}
                  >
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                      Video Preview
                    </Text>
                    <Box
                      as="iframe"
                      src={url.replace("watch?v=", "embed/")}
                      width="100%"
                      height="315"
                      border="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                )}
              </VStack>
            </CardBody>

            <Divider />

            <CardFooter>
              <Flex justify="flex-end" gap={4} w="100%">
                <Button variant="outline" onClick={() => navigate("/videos")}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Updating"
                  onClick={handleUpdate}
                >
                  Update Video
                </Button>
              </Flex>
            </CardFooter>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default UpdateVideo;
